import React from 'react';
import { cn } from '@/lib/utils';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Skeleton variant */
  variant?: 'text' | 'circular' | 'rectangular' | 'custom';
  /** Width of the skeleton (can be string or number) */
  width?: string | number;
  /** Height of the skeleton (can be string or number) */
  height?: string | number;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
  /** Number of skeleton instances to render */
  count?: number;
  /** Gap between multiple skeletons */
  gap?: number;
  /** Custom border radius for rectangular variant */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

const variantStyles = {
  text: 'h-4 rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-card',
  custom: '',
};

const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-card',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

export function Skeleton({
  variant = 'text',
  width,
  height,
  animation = 'shimmer',
  count = 1,
  gap = 8,
  rounded,
  className,
  style,
  ...props
}: SkeletonProps) {
  const skeletonElement = (
    <div
      className={cn(
        // Base styles - theme-aware background
        'bg-bg-elevated',
        // Variant styles
        variantStyles[variant],
        // Custom rounded override
        rounded && roundedClasses[rounded],
        // Default dimensions based on variant
        variant === 'text' && !height && 'h-4',
        variant === 'circular' && !width && !height && 'w-12 h-12',
        variant === 'rectangular' && !width && !height && 'w-full h-20',
        // Animations - theme-aware gradient
        animation === 'shimmer' &&
          'animate-shimmer bg-linear-to-r from-bg-elevated via-bg-accent to-bg-elevated bg-[length:1000px_100%]',
        animation === 'pulse' && 'animate-skeleton-pulse',
        // Reduced motion support
        'motion-reduce:animate-none',
        className
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      aria-hidden="true"
      {...props}
    />
  );

  if (count === 1) {
    return skeletonElement;
  }

  return (
    <div className="flex flex-col" style={{ gap: `${gap}px` }}>
      {Array.from({ length: count }, (_, index) => (
        <React.Fragment key={index}>{skeletonElement}</React.Fragment>
      ))}
    </div>
  );
}

// Pre-built skeleton compositions

/** Skeleton for a card with avatar and text */
export interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Show avatar */
  showAvatar?: boolean;
  /** Number of text lines */
  lines?: number;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonCard({
  showAvatar = true,
  lines = 3,
  animation = 'shimmer',
  className,
  ...props
}: SkeletonCardProps) {
  return (
    <div
      className={cn('p-4 rounded-lg border border-border bg-bg-elevated', className)}
      {...props}
    >
      <div className="flex gap-3">
        {showAvatar && (
          <Skeleton variant="circular" width={40} height={40} animation={animation} />
        )}
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="30%" height={16} animation={animation} />
          <div className="space-y-1">
            {Array.from({ length: lines }, (_, index) => (
              <Skeleton
                key={index}
                variant="text"
                width={index === lines - 1 ? '70%' : '100%'}
                height={14}
                animation={animation}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Skeleton for table rows */
export interface SkeletonTableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of rows */
  rows?: number;
  /** Number of columns */
  columns?: number;
  /** Show header */
  showHeader?: boolean;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeader = true,
  animation = 'shimmer',
  className,
  ...props
}: SkeletonTableProps) {
  return (
    <div className={cn('w-full', className)} {...props}>
      {showHeader && (
        <div className="flex gap-4 p-4 border-b border-border">
          {Array.from({ length: columns }, (_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={`${100 / columns}%`}
              height={14}
              animation={animation}
            />
          ))}
        </div>
      )}
      <div className="divide-y divide-bg-elevated">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className="flex gap-4 p-4">
            {Array.from({ length: columns }, (_, colIndex) => (
              <Skeleton
                key={colIndex}
                variant="text"
                width={`${100 / columns}%`}
                height={14}
                animation={animation}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for chart/graph placeholder */
export interface SkeletonChartProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Chart height */
  height?: number;
  /** Show axes */
  showAxes?: boolean;
  /** Number of bars (for bar chart style) */
  bars?: number;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonChart({
  height = 300,
  showAxes = true,
  bars = 6,
  animation = 'shimmer',
  className,
  ...props
}: SkeletonChartProps) {
  return (
    <div
      className={cn('w-full relative', className)}
      style={{ height: `${height}px` }}
      {...props}
    >
      {showAxes && (
        <>
          {/* Y-axis */}
          <div className="absolute left-0 top-0 bottom-8 w-px bg-border" />
          {/* X-axis */}
          <div className="absolute left-0 right-0 bottom-8 h-px bg-border" />
        </>
      )}
      {/* Bars */}
      <div className="absolute inset-0 bottom-8 left-8 right-8 flex items-end justify-around gap-2">
        {Array.from({ length: bars }, (_, index) => {
          const heightPercent = Math.random() * 60 + 20; // Random height between 20-80%
          return (
            <Skeleton
              key={index}
              variant="rectangular"
              width={`${100 / bars - 2}%`}
              height={`${heightPercent}%`}
              animation={animation}
              className="!rounded-t-button !rounded-b-none"
            />
          );
        })}
      </div>
      {/* X-axis labels */}
      {showAxes && (
        <div className="absolute bottom-0 left-8 right-8 flex justify-around">
          {Array.from({ length: bars }, (_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={20}
              height={10}
              animation={animation}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Skeleton for list items */
export interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of items */
  items?: number;
  /** Show icon/bullet */
  showIcon?: boolean;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonList({
  items = 4,
  showIcon = true,
  animation = 'shimmer',
  className,
  ...props
}: SkeletonListProps) {
  return (
    <div className={cn('space-y-3', className)} {...props}>
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          {showIcon && (
            <Skeleton variant="circular" width={24} height={24} animation={animation} />
          )}
          <Skeleton
            variant="text"
            width={`${70 + Math.random() * 30}%`}
            height={14}
            animation={animation}
          />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for form fields */
export interface SkeletonFormProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of fields */
  fields?: number;
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonForm({
  fields = 3,
  animation = 'shimmer',
  className,
  ...props
}: SkeletonFormProps) {
  return (
    <div className={cn('space-y-4', className)} {...props}>
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="space-y-1.5">
          <Skeleton variant="text" width={80} height={12} animation={animation} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            animation={animation}
          />
        </div>
      ))}
      <Skeleton
        variant="rectangular"
        width={120}
        height={40}
        animation={animation}
        className="!rounded-button"
      />
    </div>
  );
}

/** Skeleton for media/image placeholder */
export interface SkeletonMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Aspect ratio */
  aspectRatio?: '16/9' | '4/3' | '1/1' | '3/4' | '9/16';
  /** Animation type */
  animation?: 'shimmer' | 'pulse' | 'none';
}

export function SkeletonMedia({
  aspectRatio = '16/9',
  animation = 'shimmer',
  className,
  ...props
}: SkeletonMediaProps) {
  const aspectRatioMap = {
    '16/9': 'aspect-video',
    '4/3': 'aspect-[4/3]',
    '1/1': 'aspect-square',
    '3/4': 'aspect-[3/4]',
    '9/16': 'aspect-[9/16]',
  };

  return (
    <Skeleton
      variant="rectangular"
      className={cn('w-full', aspectRatioMap[aspectRatio], className)}
      animation={animation}
      {...props}
    />
  );
}
